<template>
  <div class="cost-report-page">
    <div class="header">
      <h1>Cost Report</h1>
      <div class="date-selector">
        <select v-model="selectedMonth" class="select">
          <option v-for="(m, i) in months" :key="i" :value="m">{{ m }}</option>
        </select>
        <select v-model="selectedYear" class="select">
          <option v-for="(y, i) in years" :key="i" :value="y">{{ y }}</option>
        </select>
        <button class="button" @click="fetchCostReport">Retrieve</button>
      </div>
    </div>
    <template v-if="loading">
      <div class="loader-container">
        <InlineLoader width="60px" height="60px" border="8px" />
      </div>
    </template>
    <template v-else-if="costSummary">
      <div class="cost-summary">
        <div class="cost-summary-table">
          <div class="cost-row"><span class="cost-key">Year</span><span class="cost-value">{{ costSummary.year }}</span></div>
          <div class="cost-row"><span class="cost-key">Month</span><span class="cost-value">{{ months[costSummary.month-1] }}</span></div>
          <div class="cost-row"><span class="cost-key">AWS Total Cost</span><span class="cost-value">${{ costSummary.awsTotalCost.toFixed(2) }}</span></div>
          <div class="cost-row"><span class="cost-key">S3 Cost</span><span class="cost-value">${{ costSummary.s3Cost.toFixed(2) }}</span></div>
          <div class="cost-row"><span class="cost-key">Database Cost</span><span class="cost-value">${{ costSummary.databaseCost.toFixed(2) }}</span></div>
          <div class="cost-row"><span class="cost-key">Other Costs</span><span class="cost-value">${{ costSummary.otherCosts.toFixed(2) }}</span></div>
        </div>
      </div>
      <div class="user-costs">
        <h3>User Costs</h3>
        <div v-for="user in userRows" :key="user.username" class="user-row">
          <div class="user-header" @click="expandedUsers.includes(user.username) ? expandedUsers.splice(expandedUsers.indexOf(user.username), 1) : expandedUsers.push(user.username)">
            <span class="collapse-icon">{{ expandedUsers.includes(user.username) ? '▼' : '▶' }}</span>
            <span class="user-email">{{ user.username }}</span>
            <span class="user-orgid">Org ID: {{ user.entries[0]?.org_id }}</span>
            <span class="user-cost">${{ user.totalCost.toFixed(2) }}</span>
          </div>
          <div v-if="expandedUsers.includes(user.username)" class="user-details">
            <table class="user-cost-table">
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Cost Type</th>
                  <th>Date</th>
                  <th>Duration</th>
                  <th>Cost</th>
                  <th>Price</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(entry, idx) in user.entries" :key="idx">
                  <td>{{ entry.description }}</td>
                  <td>{{ entry.cost_type }}</td>
                  <td>{{ entry.date ? new Date(entry.date).toLocaleString() : '' }}</td>
                  <td>{{ entry.duration.toFixed(2) }}</td>
                  <td>${{ entry.cost.toFixed(2) }}</td>
                  <td>${{ entry.price.toFixed(2) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </template>
    <template v-else>
      <div>No data available.</div>
    </template>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import InlineLoader from '@/components/InlineLoader.vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import { useToast } from 'vue-toast-notification';

const costReport = ref([]);
const columns = ref([]);
const costSummary = ref(null);
const userRows = ref([]);
const expandedUsers = ref([]);
const loading = ref(true);
const errorMessage = ref("");
const dataConnect = new DatastoreConnect();
const $toast = useToast();

const months = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];
const currentYear = new Date().getFullYear();
const years = Array.from({ length: 6 }, (_, i) => currentYear - i);
const selectedMonth = ref(months[new Date().getMonth()]);
const selectedYear = ref(currentYear);

async function fetchCostReport() {
  loading.value = true;
  errorMessage.value = "";
  costSummary.value = null;
  userRows.value = [];
  expandedUsers.value = [];
  try {
    const monthInt = months.indexOf(selectedMonth.value) + 1;
    const yearInt = selectedYear.value;
    const response = await dataConnect.api('admin.costs', { month: monthInt, year: yearInt });
    if (response && response.error && response.error.message) {
      errorMessage.value = response.error.message;
      $toast.error(response.error.message, { position: 'top' });
    } else if (response && response.result) {
      const r = response.result;
      costSummary.value = {
        year: r.year,
        month: r.month,
        awsTotalCost: r.aws_total_cost,
        s3Cost: r.s3_cost,
        databaseCost: r.database_cost,
        otherCosts: r.other_costs,
        orgId: r.org_id || (r.user_costs && Object.values(r.user_costs)[0]?.[0]?.org_id) || ''
      };
      // Parse user costs and sort by username ascending
      const users = Object.entries(r.user_costs || {}).map(([username, entries]) => {
        const totalCost = entries.reduce((sum, entry) => sum + (entry.cost || 0), 0);
        return {
          username,
          totalCost,
          entries
        };
      });
      userRows.value = users.sort((a, b) => a.username.localeCompare(b.username));
    }
  } catch (error) {
    errorMessage.value = 'Failed to fetch cost report.';
    $toast.error('Failed to fetch cost report.', { position: 'top' });
    costSummary.value = null;
    userRows.value = [];
    expandedUsers.value = [];
    console.error('Failed to fetch cost report:', error);
  } finally {
    loading.value = false;
  }
}

onMounted(fetchCostReport);
</script>

<style scoped>
.cost-report-page {
  padding: 32px;
  max-height: 100vh;
  overflow-y: auto;
}
.header {
  font-size: 1.2rem;
  font-weight: 700;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
}
.date-selector {
  width: 380px;
  display: flex;
  flex-direction: row;
  margin-left: 20px;
  font-size: 1rem;
  font-weight: 500;
}
.date-selector select + select {
  margin-left: 20px;
}
.date-selector button {
  margin-left: 20px;
}
.cost-report-table {
  width: 100%;
  border-collapse: collapse;
}
.cost-report-table th, .cost-report-table td {
  border: 1px solid #ccc;
  padding: 8px;
  text-align: left;
}
.loader-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 120px;
}

.cost-summary {
  margin-bottom: 24px;
  font-size: 1.1rem;
}
.cost-summary-table {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 24px;
  max-width: 400px;
}
.cost-row {
  display: contents;
}
.cost-key {
  font-weight: 600;
  text-align: right;
}
.cost-value {
  text-align: left;
}
.user-costs {
  margin-top: 24px;
}
.user-row {
  border-bottom: 1px solid #eee;
  padding: 2px 0;
}
.user-header {
  display: flex;
  align-items: center;
  cursor: pointer;
  gap: 16px;
  font-size: 1rem;
  background: #adc1cf;
  border-radius: 6px;
  padding: 4px 12px;
}
.collapse-icon {
  font-size: 1.1rem;
  width: 24px;
  text-align: center;
}
.user-email {
  font-weight: 600;

}
.user-cost {
  font-weight: 500;
  color: #01070e;
}
.user-details {
  margin-left: 10px;
  margin-top: 4px;
  font-size: 0.98rem;
}
.user-cost-table {
  width: 100%;

  border-radius: 8px;
  margin-top: 2px;
  box-shadow: 1px 1px 4px 1px rgba(0, 0, 0, 0.2), 0 0 1px 1px rgba(0, 0, 0, 0.05);
  background: #f7fafd;
  color: #222;
}

.user-cost-table thead > tr > th {
  font-weight: 600;
  font-size: 1rem;
  background: #e3ecf7;
  padding: 4px 4px;
  border-bottom: 2px solid #adc1cf;
}
.user-cost-table tbody tr:nth-of-type(odd) {
  background-color: rgba(0, 0, 0, .05);
}
.user-cost-table tbody tr {
  height: 32px !important;
}
.user-cost-table td {
  padding-top: 4px !important;
  padding-bottom: 4px !important;
  padding-left: 12px !important;
  padding-right: 12px !important;
  line-height: 1.3 !important;
  border: none;
}
.user-orgid {
  margin-left: 16px;
  font-size: 0.98rem;
  color: #2b2b2b;
}
.user-cost-table th:nth-child(2),
.user-cost-table td:nth-child(2) {
  text-align: center;
}
.user-cost-table th:nth-child(5),
.user-cost-table td:nth-child(5),
.user-cost-table th:nth-child(6),
.user-cost-table td:nth-child(6),
.user-cost-table th:nth-child(4),
.user-cost-table td:nth-child(4) {
  text-align: right;
}
.user-cost-table th:nth-child(3),
.user-cost-table td:nth-child(3) {
  max-width: 200px;
  width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
}
</style>
